/**
 * The people console (ADR-0067, A2).
 *
 * `GET/POST /ops/people` and the three acts on one person live there; a person's facets are
 * amended through `PATCH /ops/principals/:id/facets`, the one writer of that column. No
 * justification header on any of them: `principal_bindings` is platform-scoped, with no tenant to
 * cross. **Nothing here ever carries an email back** — the list says what authority a colleague
 * holds, not how to reach them.
 */

import { apiFetch } from './client.js';

export interface PersonRow {
  readonly principalId: string;
  readonly label: string | null;
  readonly state: string;
  readonly facets: readonly string[];
  readonly platformStaff: boolean;
  /** Written by `OPS_BOOTSTRAP_EMAIL` rather than by a person. */
  readonly founding: boolean;
  readonly createdAt: string;
  readonly revokedAt: string | null;
}

export interface RevocationResult {
  readonly principalId: string;
  readonly status: 'revoked';
  readonly sessionsRevoked: number;
  readonly bindingChanged: boolean;
}

export interface FacetAmendment {
  readonly principalId: string;
  readonly facets: readonly string[];
  readonly gained: readonly string[];
  readonly lost: readonly string[];
}

export function listPeople(): Promise<{ readonly items: readonly PersonRow[] }> {
  return apiFetch('/ops/people');
}

export function invitePerson(body: {
  readonly email: string;
  readonly label: string;
  readonly facets: readonly string[];
  readonly note: string;
}): Promise<PersonRow> {
  return apiFetch('/ops/people', { method: 'POST', body: JSON.stringify(body) });
}

export function withdrawInvitation(principalId: string, note: string): Promise<PersonRow> {
  return apiFetch(`/ops/people/${principalId}/withdraw`, { method: 'POST', body: JSON.stringify({ note }) });
}

export function revokePerson(principalId: string, reasonCode: string): Promise<RevocationResult> {
  return apiFetch(`/ops/people/${principalId}/revoke`, { method: 'POST', body: JSON.stringify({ reasonCode }) });
}

export function setPlatformStaff(principalId: string, platformStaff: boolean, note: string): Promise<PersonRow> {
  return apiFetch(`/ops/people/${principalId}/staff`, {
    method: 'PATCH',
    body: JSON.stringify({ platformStaff, note }),
  });
}

export function amendFacets(principalId: string, facets: readonly string[], note: string): Promise<FacetAmendment> {
  return apiFetch(`/ops/principals/${principalId}/facets`, {
    method: 'PATCH',
    body: JSON.stringify({ facets, note }),
  });
}

/** Accept or decline an invitation waiting for the signed-in person (ADR-0067, amended). */
export function answerInvitation(
  invitationId: string,
  verb: 'accept' | 'decline',
): Promise<{ readonly answer: 'accepted' | 'declined'; readonly signedOut: boolean }> {
  return apiFetch(`/auth/invitations/${invitationId}/${verb}`, { method: 'POST', body: '{}' });
}
